// frontend/src/components/layout/NotificationBell.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

// Module-level cache survives component unmount/remount during navigation
let cachedData = null;
let lastFetchedAt = 0;
const STALE_THRESHOLD_MS = 60 * 1000; // Only re-fetch if >60s since last fetch

// Clear module-level cache on logout to prevent stale data from previous user
export function clearNotificationCache() {
  cachedData = null;
  lastFetchedAt = 0;
}

const TYPE_CONFIG = {
  overdue:       { color: 'text-red-600',    bg: 'bg-red-50',    icon: '!' },
  due_soon:      { color: 'text-amber-600',  bg: 'bg-amber-50',  icon: '\u25B2' },
  match_pending: { color: 'text-blue-600',   bg: 'bg-blue-50',   icon: '\u2194' },
  overdraft_risk:{ color: 'text-red-700',    bg: 'bg-red-100',   icon: '\u25BC' },
};

function NotificationItem({ item, onAction }) {
  const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.overdue;
  return (
    <button
      onClick={() => onAction(item)}
      className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0`}
    >
      <span className={`flex-shrink-0 w-8 h-8 rounded-full ${cfg.bg} ${cfg.color} flex items-center justify-center text-sm font-bold`}>
        {cfg.icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 leading-snug truncate">{item.label}</p>
        {item.amount != null && (
          <p className={`text-xs mt-0.5 ${cfg.color} font-medium`}>
            ${Math.abs(item.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        )}
      </div>
    </button>
  );
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(() => cachedData || { unreadCount: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const drawerRef = useRef(null);
  const nav = useNavigate();

  const fetchInbox = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/notifications/inbox');
      setData(res.data);
      cachedData = res.data;
      lastFetchedAt = Date.now();
    } catch {
      // Silently fail — bell just shows 0
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch (skip if recently fetched) + refetch on window focus
  useEffect(() => {
    const age = Date.now() - lastFetchedAt;
    if (age > STALE_THRESHOLD_MS) {
      fetchInbox();
    }
    const handleFocus = () => {
      if (Date.now() - lastFetchedAt > STALE_THRESHOLD_MS) {
        fetchInbox();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchInbox]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleAction = (item) => {
    setOpen(false);
    if (item.type === 'overdue' || item.type === 'due_soon') {
      nav('/planner/forecast');
    } else if (item.type === 'match_pending') {
      nav('/planner/register');
    } else if (item.type === 'overdraft_risk') {
      nav('/planner/forecast');
    }
  };

  return (
    <div className="relative" ref={drawerRef}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50"
        aria-label={`Notifications${data.unreadCount > 0 ? ` (${data.unreadCount})` : ''}`}
      >
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 22a2 2 0 0 0 2-2H10a2 2 0 0 0 2 2zm6-6V11a6 6 0 1 0-12 0v5l-2 2v1h16v-1l-2-2z" />
        </svg>
        {data.unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none px-1 shadow-sm">
            {data.unreadCount > 9 ? '9+' : data.unreadCount}
          </span>
        )}
      </button>

      {/* Drawer */}
      {open && (
        <>
          {/* Backdrop - mobile only */}
          <div className="fixed inset-0 bg-black/30 z-[60] sm:hidden" onClick={() => setOpen(false)} />

          {/* Panel */}
          <div className={`
            fixed sm:absolute z-[70]
            inset-x-0 bottom-0 pb-[env(safe-area-inset-bottom)] sm:pb-0
            sm:inset-auto sm:right-0 sm:top-full sm:mt-2
            w-full sm:w-80
            max-h-[70vh] sm:max-h-[420px]
            bg-white rounded-t-2xl sm:rounded-xl shadow-2xl
            flex flex-col overflow-hidden
            border border-gray-200
          `}>
            {/* Handle bar - mobile */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>

            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                Notifications {data.unreadCount > 0 && `(${data.unreadCount})`}
              </h3>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-gray-100 text-gray-400"
                aria-label="Close notifications"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto">
              {loading && data.items.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">Loading...</div>
              ) : data.items.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">
                  You're all caught up!
                </div>
              ) : (
                data.items.map((item) => (
                  <NotificationItem key={item.id} item={item} onAction={handleAction} />
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
